// src/lib/intercom.ts
//
// Lazy-loading Intercom widget with safe wrappers.
// No-ops if VITE_INTERCOM_APP_ID is not set.
// Hides default launcher; uses custom_launcher_selector for TopBar trigger.
//
// Intercom is an untyped third-party global — window.Intercom casts are unavoidable.
/* eslint-disable @typescript-eslint/no-explicit-any */

const APP_ID = import.meta.env.VITE_INTERCOM_APP_ID as string | undefined;
const API_BASE = import.meta.env.VITE_INTERCOM_API_BASE as string | undefined;

/** Whether Intercom is configured (app ID present) */
export const isIntercomConfigured = !!APP_ID;

/** Track script loading state via shared promise */
let loadPromise: Promise<void> | null = null;
let loaded = false;

/** Track boot state — prevents show/update before boot completes */
let booted = false;

/** Shared boot promise — dedupes concurrent boot attempts (background + click) */
let bootPromise: Promise<boolean> | null = null;

/** Distinguish real Intercom SDK from the queue stub we install before script loads */
function isRealIntercom(fn: unknown): boolean {
  return typeof fn === 'function' && !(fn as any).q;
}

/**
 * Lazy-load the Intercom widget script.
 * Idempotent — safe to call multiple times.
 * Concurrent callers share the same promise (no duplicate script tags).
 */
function loadScript(): Promise<void> {
  if (loaded) return Promise.resolve();
  if (loadPromise) return loadPromise;
  if (!APP_ID) return Promise.resolve();

  loadPromise = new Promise<void>((resolve) => {
    const w = window as any;

    // Set intercomSettings before script loads so the SDK sees app_id immediately
    w.intercomSettings = {
      app_id: APP_ID,
      ...(API_BASE ? { api_base: API_BASE } : {}),
    };

    // Real Intercom already present (e.g. loaded by another path) — skip script injection
    if (isRealIntercom(w.Intercom)) {
      loaded = true;
      loadPromise = null;
      resolve();
      return;
    }

    // Initialize Intercom command queue (stub — has .q property)
    const ic = function (...args: unknown[]) { ic.c(args); } as any;
    ic.q = [] as unknown[];
    ic.c = function (args: unknown[]) { ic.q.push(args); };
    w.Intercom = ic;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${APP_ID}`;

    script.onload = () => {
      loaded = true;
      loadPromise = null;
      resolve();
    };

    script.onerror = () => {
      // Intercom blocked (ad blocker / network) — degrade gracefully
      loadPromise = null;
      // Clear stale queue stub so future retries don't false-positive
      if (w.Intercom?.q) {
        delete w.Intercom;
      }
      resolve();
    };

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(script, firstScript);
  });

  return loadPromise;
}

/** Check if Intercom is actually available (real SDK loaded, not just queue stub) */
export function isIntercomAvailable(): boolean {
  return loaded && isRealIntercom((window as any).Intercom);
}

/** Check if Intercom has been successfully booted with a user identity */
export function isIntercomBooted(): boolean {
  return booted && isRealIntercom((window as any).Intercom);
}

/**
 * Poll for Intercom boot completion.
 * Returns true if booted within timeout, false otherwise.
 */
export function waitForIntercomBoot(timeoutMs = 8000, intervalMs = 100): Promise<boolean> {
  if (isIntercomBooted()) return Promise.resolve(true);

  return new Promise<boolean>((resolve) => {
    const start = Date.now();
    const timer = setInterval(() => {
      if (isIntercomBooted()) {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - start >= timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, intervalMs);
  });
}

/**
 * Boot Intercom with identity verification (JWT).
 * Returns true if boot succeeded, false if it was a no-op or failed.
 */
export async function bootIntercom(jwt: string, userId: string, email?: string): Promise<boolean> {
  if (!APP_ID) return false;

  await loadScript();
  if (!isIntercomAvailable()) return false;

  const settings: Record<string, any> = {
    app_id: APP_ID,
    user_id: userId,
    intercom_user_jwt: jwt,
    hide_default_launcher: true,
    custom_launcher_selector: '#filmple-support-launcher',
  };

  if (API_BASE) {
    settings.api_base = API_BASE;
  }

  if (email) {
    settings.email = email;
  }

  try {
    // Merge full settings into intercomSettings so the SDK has consistent state
    const w = window as any;
    w.intercomSettings = {
      ...(w.intercomSettings || {}),
      ...settings,
    };

    (window as any).Intercom('boot', settings);
    booted = true;
    return true;
  } catch {
    booted = false;
    return false;
  }
}

/**
 * Ensure Intercom is booted — dedupes with any in-flight boot attempt.
 * Safe to call from both the background IntercomBoot component and the click handler.
 * Returns true if Intercom is booted after this call, false otherwise.
 */
export async function ensureIntercomBoot(jwt: string, userId: string, email?: string): Promise<boolean> {
  if (isIntercomBooted()) return true;
  if (bootPromise) return bootPromise;

  bootPromise = bootIntercom(jwt, userId, email).finally(() => {
    bootPromise = null;
  });

  return bootPromise;
}

/**
 * Update Intercom (e.g. on route change).
 * No-op unless Intercom has been successfully booted.
 * Throttled externally — caller is responsible for rate limiting.
 */
export function updateIntercom(): void {
  if (!isIntercomBooted()) return;
  (window as any).Intercom('update');
}

/**
 * Show the Intercom messenger.
 * Returns true if messenger was opened, false if not booted.
 */
export function showIntercom(): boolean {
  if (!isIntercomBooted()) return false;
  (window as any).Intercom('show');
  return true;
}

/**
 * Shutdown Intercom — call on logout / user switch.
 * Always clears local boot flags to prevent stale state, even if SDK is unavailable.
 */
export function shutdownIntercom(): void {
  booted = false;
  if (isIntercomAvailable()) {
    (window as any).Intercom('shutdown');
  }
}
